import React, { FC, useRef, useEffect, useState } from 'react';
import ReactDOM from 'react-dom';
import cn from 'classnames';
import FocusLock from 'react-focus-lock';
import { RemoveScroll } from 'react-remove-scroll';
import Clear from '@crehana/crehana-icons/lib/Clear';
import { DialogProps } from './types';
import { Container, Row, Col } from '../GridV2';
import { baseLighter } from '../styles/colors';
import parseClassnames from '../helpers/parseClassnames';
import { KEY_CODES } from '../helpers/constants';
import { DIALOG_SHEET_COL_PROPS } from './constants';

import useWindowInnerHeight from '../hooks/useWindowInnerHeight';

const Dialog: FC<DialogProps> = ({
  size = 'm',
  open = false,
  onDismiss,
  onEscape,
  hideCloseBtn = false,
  ignoreEscape = false,
  ignoreBackdropClick = false,
  hideBackdrop = false,
  hideDialogSheet = false,
  rootClassName = '',
  className = '',
  backdropClassName = '',
  disabledScrollLock,
  disabledFocusLock,
  children,
  containerHTMLProps = {},
  dark,
  datatestid,
  'data-testid': dataTestId,
  ...other
}) => {
  const windowInnerHeight = useWindowInnerHeight();
  const [isClient, setIsClient] = useState(false);
  const dialogSheetRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setIsClient(true);
  }, []);

  useEffect(() => {
    const clickOutsideListener = (
      event:
        | HTMLElementEventMap['mousedown']
        | HTMLElementEventMap['touchstart'],
    ) => {
      if (
        dialogSheetRef.current &&
        !dialogSheetRef.current.contains(event.target as Node) &&
        onDismiss
      ) {
        onDismiss();
      }
    };

    if (open && !ignoreBackdropClick && size !== 'full') {
      document.addEventListener('mousedown', clickOutsideListener);
      document.addEventListener('touchstart', clickOutsideListener);
    } else {
      document.removeEventListener('mousedown', clickOutsideListener);
      document.removeEventListener('touchstart', clickOutsideListener);
    }

    return () => {
      document.removeEventListener('mousedown', clickOutsideListener);
      document.removeEventListener('touchstart', clickOutsideListener);
    };
  }, [ignoreBackdropClick, onDismiss, open, size]);

  const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
    if (event.keyCode === KEY_CODES.ESCAPE) {
      event.stopPropagation();
      if (typeof onEscape === 'function') {
        onEscape(event);
      }
      if (open && !ignoreEscape && typeof onDismiss === 'function') {
        onDismiss();
      }
    }
  };

  // SSR support
  if (!isClient || typeof document === 'undefined') {
    return null;
  }

  const renderCloseButton = () => {
    let buttonClasses = 'mr-20 mt-40 sm:mr-24 sm:mt-24';
    let iconClasses = 'w-24 h-24 sm:w-32 sm:h-32';

    if (size === 's') {
      buttonClasses = 'mr-8 mt-8 sm:mr-16 sm:mt-16';
      iconClasses = 'w-24 h-24';
    }
    if (size === 'full') {
      buttonClasses = 'mr-16 mt-12 sm:mt-32 sm:mr-32';
      iconClasses += ' lg:w-48 lg:h-48';
    }
    return (
      <button
        type="button"
        className={`focus:outline-none absolute top-0 right-0 z-dialog ${buttonClasses}`}
        onClick={onDismiss}
        data-testid="close-dialog"
      >
        <Clear className={iconClasses} color={baseLighter} />
      </button>
    );
  };

  const renderChildren = () => (open ? children : null);

  return ReactDOM.createPortal(
    <div
      className={parseClassnames(
        rootClassName,
        cn('fixed inset-0 z-dialog', open ? 'cui-is-visible' : 'cui-is-hidden'),
      )}
      role="dialog"
      {...containerHTMLProps}
    >
      {!hideBackdrop && (
        <div
          className={parseClassnames(
            backdropClassName,
            'fixed inset-0 bg-base-main opacity-80 -z-1',
          )}
          tabIndex={-1}
        />
      )}
      <FocusLock
        disabled={disabledFocusLock || open}
        returnFocus
        className="h-full"
      >
        <RemoveScroll enabled={!disabledScrollLock && open} forwardProps>
          <div
            className="absolute top-0 right-0 left-0 "
            css={`
              transform: translateY(0);
              animation: height ease 0.2s;
              height: ${windowInnerHeight}px;
            `}
          >
            {hideDialogSheet ? (
              renderChildren()
            ) : (
              <Container
                className="relative h-full items-center z-1 focus:outline-none"
                full={size === 'full' || { xs: size !== 's', sm: false }}
                ignoreKeyboardDebug={size === 'full'}
                role="document"
                tabIndex={-1}
                onKeyDown={handleKeyDown}
                data-testid={dataTestId || datatestid}
              >
                <Row className="h-full items-center" nowrap>
                  <Col {...DIALOG_SHEET_COL_PROPS[size]}>
                    <div
                      ref={dialogSheetRef}
                      className={parseClassnames(
                        className,
                        cn(
                          'relative overflow-y-auto overflow-x-hidden',
                          dark
                            ? 'bg-base-dark text-white'
                            : 'bg-white text-base-main',
                          size === 's' ? 'max-h-90vh' : 'h-full max-h-screen',
                          {
                            'sm:h-auto sm:min-h-0 sm:max-h-90vh':
                              size !== 'full',
                          },
                        ),
                      )}
                      {...other}
                      style={{
                        height: size === 'full' ? windowInnerHeight : '',
                        ...other.style,
                      }}
                    >
                      {!hideCloseBtn && renderCloseButton()}
                      {renderChildren()}
                    </div>
                  </Col>
                </Row>
              </Container>
            )}
          </div>
        </RemoveScroll>
      </FocusLock>
    </div>,
    document.body,
  );
};

export default Dialog;
